import { cloneDeep, keyBy, sortBy, uniqBy } from "lodash";
import { useCallback, useState } from "react";
import { useParams } from "react-router-dom";
import {
  createModules as createAnalysisModules,
  transformSubModule as transformAnalysisSubModule
} from "./analysis";
import {
  createModules as createCompsModules,
  transformSubModule as transformCompSubModule
} from "./comps";
import {
  createModules as createGuidanceModules,
  transformSubModule as transformGuidanceSubModule
} from "./guidance";
import useListings from "./listings";
import useNavigation from "./navigation";
import {
  createModules as createRdcModules,
  transformSubModule as transformRdcSubModule
} from "./rdc";
import {
  updateListingVisibility,
  updateListingsOrder,
  updateModuleAndSubModuleLinks
} from "./utils";
import { getClosedListings } from "../../../utils/listing";

function useModules({ reportId, agent }) {
  const { guid } = useParams();
  const [modules, setModules] = useState([]);
  const navigation = useNavigation(modules);

  const visibleModules = modules
    .map((module) => ({
      ...module,
      subModules: module.subModules.filter((subModule) => !subModule.isHidden)
    }))
    .filter((module) => !module.isHidden);

  const createModules = useCallback(
    ({
      listings,
      pages,
      links,
      disallowedPages,
      isAcreageReport,
      isWebLead
    }) => {
      const filteredGuidancePages = isWebLead
        ? pages.filter((page) => {
            return ![
              "comps",
              "average-pricing",
              "average-price-per-sqft",
              "average-price-per-acre",
              "time-to-sell",
              "online-valuations",
              "suggested-list-price"
            ].includes(page.key);
          })
        : pages.filter((page) =>
            isAcreageReport
              ? page.key !== "average-price-per-sqft"
              : page.key !== "average-price-per-acre"
          );

      const compsModules = createCompsModules({
        guid,
        subModules: getClosedListings(listings),
        pages,
        links,
        isWebLead
      });
      const analysisModules = createAnalysisModules({
        guid,
        links,
        isAcreageReport,
        pages,
        isWebLead
      });
      const guidanceModules = createGuidanceModules({
        guid,
        modules: filteredGuidancePages,
        pages,
        links,
        isWebLead
      });
      const rdcModules = createRdcModules({
        guid,
        links,
        isAcreageReport,
        pages,
        isWebLead,
        canViewModules: agent.hasRdcToolkit
      });
      const allModules = [
        ...compsModules,
        ...analysisModules,
        ...guidanceModules,
        ...rdcModules
      ].filter((module) => !disallowedPages.includes(module.key));
      const moduleKeys = filteredGuidancePages.map(({ key }) => key);
      const uniqueModules = uniqBy(allModules, "key");
      const sortedModules = sortModules(uniqueModules, moduleKeys);
      setModules(sortedModules);
    },
    []
  );

  const reorderModules = useCallback(
    (startIndex, endIndex) => {
      const newModules = cloneDeep(modules);
      const [removed] = newModules.splice(startIndex, 1);
      newModules.splice(endIndex, 0, removed);
      setModules(newModules);
      updateModuleAndSubModuleLinks(reportId, newModules);
    },
    [reportId, modules]
  );

  const updateSubModules = useCallback((moduleKey, updater) => {
    setModules((modules) =>
      modules.map((module) => {
        if (module.key === moduleKey) {
          return { ...module, subModules: module.subModules.map(updater) };
        }
        return module;
      })
    );
  }, []);

  const reorderSubModules = useCallback(
    (moduleKey, startIndex, endIndex) => {
      const module = modules.find((module) => module.key === moduleKey);
      const newSubModules = cloneDeep(module.subModules);
      const [removed] = newSubModules.splice(startIndex, 1);
      newSubModules.splice(endIndex, 0, removed);
      const newModules = modules.map((module) => {
        if (module.key === moduleKey) {
          return { ...module, subModules: newSubModules };
        }
        return module;
      });
      setModules(newModules);
      updateModuleAndSubModuleLinks(reportId, newModules);

      if (moduleKey === "comps") {
        const listingRecordIds = newSubModules.map(
          (subModule) => subModule.data.id
        );
        updateListingsOrder(reportId, listingRecordIds);
      }
    },
    [reportId, modules]
  );

  const getModuleData = useCallback(
    (pageKey) => {
      const module = modules.find((module) => module.key === pageKey) || {};
      return module.data;
    },
    [modules]
  );

  const getModuleConfig = useCallback(
    (pageKey) => {
      const module = modules.find((module) => module.key === pageKey) || {};
      return module.config;
    },
    [modules]
  );

  const toggleModule = useCallback(
    (moduleKey) => {
      const newModules = modules.map((module) => {
        if (module.key === moduleKey) {
          return { ...module, isHidden: !module.isHidden };
        }
        return module;
      });
      setModules(newModules);
      updateModuleAndSubModuleLinks(reportId, newModules);
    },
    [reportId, modules]
  );

  const toggleSubModule = useCallback(
    (moduleKey, subModuleKey) => {
      const newModules = modules.map((module) => {
        if (module.key === moduleKey) {
          return {
            ...module,
            subModules: module.subModules.map((subModule) => {
              if (subModule.key === subModuleKey) {
                return {
                  ...subModule,
                  isHidden: !subModule.isHidden,
                  data: {
                    ...subModule.data,
                    isHidden: !subModule.data.isHidden
                  }
                };
              }
              return subModule;
            })
          };
        }
        return module;
      });
      setModules(newModules);
      updateModuleAndSubModuleLinks(reportId, newModules);

      if (moduleKey === "comps") {
        const module = modules.find((module) => module.key === moduleKey);
        const subModule = module.subModules.find(
          (subModule) => subModule.key === subModuleKey
        );
        updateListingVisibility(reportId, subModule.data.recordId);
      }
    },
    [reportId, modules]
  );

  const addSubModule = useCallback((moduleKey, data) => {
    const transform = {
      comps: transformCompSubModule,
      analysis: transformAnalysisSubModule,
      guidance: transformGuidanceSubModule,
      rdc: transformRdcSubModule
    };
    setModules((modules) =>
      modules.map((module) => {
        if (module.key === moduleKey) {
          return {
            ...module,
            subModules: [...module.subModules, transform[moduleKey](data)]
          };
        }
        return module;
      })
    );
  }, []);

  // Derive listings from comps
  const compsModule = modules.find((module) => module.key === "comps") || {};
  const compsSubModules = compsModule.subModules || [];
  const {
    visibleListings,
    visibleListingsWithGhostListings,
    visibleListingGroups,
    listingsWithForgeListings,
    forgeListings,
    setForgeListings,
    toggleListing,
    isAddingListing,
    ghostListings,
    clearGhostListings,
    zestimateRequest,
    getSchools,
    addListingAdjustment,
    updateListingAdjustment,
    removeListingAdjustment,
    myRecentSales,
    setMyRecentSales
  } = useListings({
    reportId,
    subModules: compsSubModules,
    updateSubModules,
    addSubModule,
    toggleSubModule
  });

  return {
    modules,
    visibleModules,
    navigation,
    createModules,
    reorderModules,
    reorderSubModules,
    getModuleData,
    getModuleConfig,
    toggleModule,
    toggleSubModule,
    visibleListings,
    visibleListingsWithGhostListings,
    visibleListingGroups,
    listingsWithForgeListings,
    forgeListings,
    setForgeListings,
    toggleListing,
    isAddingListing,
    ghostListings,
    clearGhostListings,
    zestimateRequest,
    getSchools,
    addListingAdjustment,
    updateListingAdjustment,
    removeListingAdjustment,
    myRecentSales,
    setMyRecentSales
  };
}

function sortModules(modules, moduleOrder) {
  const hasSortOrder = modules.some(
    (module) => typeof module.sortOrder !== "undefined"
  );

  // If we have a sort order, sort by that instead of the default order
  if (hasSortOrder) return sortBy(modules, "sortOrder");

  const keyedModules = keyBy(modules, "key");
  const modulesInMap = moduleOrder.map((moduleKey) => keyedModules[moduleKey]);
  const modulesNotInMap = modules.filter(
    (module) => !moduleOrder.includes(module.key)
  );

  return [...modulesInMap, ...modulesNotInMap].filter(Boolean);
}

export default useModules;
